import { formatDate, formatTime } from "../../../language/time";
import { NodeType } from "../../models";
import { AccessRuleChangeSet } from "../models";
import { getEndOfDay, getStartOfDay } from "../ruleEditorTimeUtils";

const API_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
const API_TIME_FORMAT = "HH:mm:ss";

export type BaseAccessRuleRequestParams = {
  userId: string;
  operatorId: string;
  changeSet: AccessRuleChangeSet;
};

export function createEmptyChangeSet(): AccessRuleChangeSet {
  return {
    effectiveDateRange: { created: [], updated: [], deleted: [] },
    maxDays: { created: [], updated: [], deleted: [] },
    blackoutDateRange: { created: [], updated: [], deleted: [] },
    blackoutDayOfWeek: { created: [], updated: [], deleted: [] },
  };
}

export function mapIfAny<T, R>(
  items: T[] | undefined,
  mapper: (item: T) => R,
): R[] {
  return items && items.length > 0 ? items.map(mapper) : [];
}

export function mapDeletedIds<T extends { liftAccessRuleId?: number }>(
  rules: T[] | undefined,
): number[] {
  return rules?.length ? rules.map((r) => r.liftAccessRuleId!) : [];
}

export function formatApiDateTime(date: Date): string {
  return formatDate(date, API_DATETIME_FORMAT) ?? "";
}

/** Rule dates are ISO 8601 local time with no offset and cover whole days: 00:00:00 to 23:59:59. */
export function formatApiStartOfDay(date: Date): string {
  return date ? formatApiDateTime(getStartOfDay(date)) : "";
}

export function formatApiEndOfDay(date: Date): string {
  return date ? formatApiDateTime(getEndOfDay(date)) : "";
}

export function formatApiTime(date: Date): string {
  return formatTime(date, API_TIME_FORMAT) ?? "";
}

/**
 * The request time for the API's RequestUtcDateTime fields: UTC, whole seconds.
 *
 * Stored as sent, so UTC — a local offset made the stored value depend on the API
 * server's timezone. Whole seconds because SQL `datetime` rounds milliseconds to
 * ~3ms steps, which would make the stored value differ from the one sent.
 *
 * Bulk runs stamp this once for the whole run: with the user it is what groups a run
 * in the audit log.
 */
export const getRequestUtcDateTime = () =>
  new Date().toISOString().replace(/\.\d+Z$/, "Z");

export enum LIFT_ACCESS_RULE_SCOPE_CATEGORY {
  RESORT_GROUP = 1,
  RESORT = 2,
  LOCATION = 3,
}

const nodeTypeToScopeCategoryMap: Record<
  NodeType,
  LIFT_ACCESS_RULE_SCOPE_CATEGORY
> = {
  resort: LIFT_ACCESS_RULE_SCOPE_CATEGORY.RESORT,
  resortGroup: LIFT_ACCESS_RULE_SCOPE_CATEGORY.RESORT_GROUP,
  location: LIFT_ACCESS_RULE_SCOPE_CATEGORY.LOCATION,
};

export const mapNodeTypeToScopeCategory = (nodeType: NodeType) =>
  nodeTypeToScopeCategoryMap[nodeType];
